import type { NavigateFunction } from 'react-router';

import { cerrarTodoConAtras, saltoPropio } from './atras';

/**
 * Pestañas y «atrás», como en las apps nativas: Inicio es la base. Desde
 * Inicio, una pestaña se apila encima; de una pestaña a otra se sustituye
 * (no se acumulan pasos); y volver a Inicio regresa a la base. Así «atrás»
 * desde cualquier pestaña lleva a Inicio, y desde Inicio sale de la app.
 *
 * La base se recuerda por su entrada del historial (API de navegación del
 * navegador; si no la hay, por el índice del router).
 */

export const RUTA_INICIO = '/inicio';

type Navegar = NavigateFunction;

let claveBase: string | null = null;
let indiceBase: number | null = null;

function apiNavegacion(): Navigation | null {
  // No todos los navegadores la tienen (aunque los tipos digan que sí).
  return 'navigation' in window && window.navigation ? window.navigation : null;
}

function indiceRouter(): number | null {
  const estado: unknown = window.history.state;
  if (typeof estado !== 'object' || estado == null) {
    return null;
  }
  const idx = (estado as Record<string, unknown>).idx;
  return typeof idx === 'number' ? idx : null;
}

/** La entrada actual (Inicio) pasa a ser la base. */
export function marcarBase() {
  claveBase = apiNavegacion()?.currentEntry?.key ?? null;
  indiceBase = indiceRouter();
}

/** Para las pruebas. */
export function olvidarBase() {
  claveBase = null;
  indiceBase = null;
}

/**
 * Al abrir la app directamente en otra pantalla (un aviso, un acceso
 * directo), Inicio queda debajo: el primer «atrás» lleva a Inicio en vez
 * de cerrar la app.
 */
export function ponerInicioDebajo(destino: string, navegar: Navegar) {
  const colocar = () => {
    void navegar(RUTA_INICIO, { replace: true });
    marcarBase();
    void navegar(destino);
  };
  // Si ya hay algo abierto encima (p. ej. el bloqueo con PIN), se baja a la
  // entrada de la pantalla, se pone Inicio debajo y se repone la suya arriba
  // del todo para que «atrás» lo siga cerrando a él.
  const encima: unknown = window.history.state?.rumboAtras;
  if (typeof encima !== 'string') {
    colocar();
    return;
  }
  saltoPropio();
  window.addEventListener(
    'popstate',
    () => {
      colocar();
      window.history.pushState(
        { ...window.history.state, rumboAtras: encima },
        '',
      );
    },
    { once: true },
  );
  window.history.back();
}

/** Toque en una pestaña de la barra (o en Inicio). */
export function irAPestana(ruta: string, navegar: Navegar) {
  cerrarTodoConAtras(() => saltarAPestana(ruta, navegar));
}

function saltarAPestana(ruta: string, navegar: Navegar) {
  const enRuta = window.location.pathname === ruta;
  const api = apiNavegacion();
  const actual = api?.currentEntry;
  const entradas = api?.entries() ?? [];
  const base = entradas.find(e => e.key === claveBase);
  if (api && actual && base && base.index <= actual.index) {
    if (ruta === RUTA_INICIO) {
      if (actual.index > base.index) {
        saltoPropio();
        void api.traverseTo(base.key).finished?.catch(() => undefined);
      }
      return;
    }
    if (actual.index === base.index) {
      void navegar(ruta);
      return;
    }
    const encima = entradas[base.index + 1];
    if (actual.index > encima.index) {
      // Desde una subpantalla: se descarta lo de encima de la pestaña.
      saltoPropio();
      void api
        .traverseTo(encima.key)
        .finished?.then(() => void navegar(ruta, { replace: true }))
        .catch(() => undefined);
      return;
    }
    if (!enRuta) {
      void navegar(ruta, { replace: true });
    }
    return;
  }

  // Sin la API de navegación: con el índice del router.
  const idx = indiceRouter();
  if (idx != null && indiceBase != null && idx >= indiceBase) {
    if (ruta === RUTA_INICIO) {
      if (idx > indiceBase) {
        saltoPropio();
        void navegar(indiceBase - idx);
      }
      return;
    }
    if (idx === indiceBase) {
      marcarBase();
      void navegar(ruta);
      return;
    }
    if (idx > indiceBase + 1) {
      saltoPropio();
      window.addEventListener(
        'popstate',
        () => void navegar(ruta, { replace: true }),
        { once: true },
      );
      void navegar(indiceBase + 1 - idx);
      return;
    }
    if (!enRuta) {
      void navegar(ruta, { replace: true });
    }
    return;
  }

  // Sin base conocida.
  if (window.location.pathname === RUTA_INICIO) {
    marcarBase();
    if (!enRuta) {
      void navegar(ruta);
    }
    return;
  }
  void navegar(ruta, { replace: true });
  if (ruta === RUTA_INICIO) {
    marcarBase();
  }
}
